import { motion } from "framer-motion";
import { Music as MusicIcon, Headphones, Mic, Disc } from "lucide-react";

const Music = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const musicProjects = [
    {
      title: "Electronic Production",
      description:
        "Creating electronic music using modern DAWs and synthesizers",
      icon: MusicIcon,
      color: "text-primary-600",
      bgColor: "bg-primary-50",
    },
    {
      title: "Live Performance",
      description: "Performing live music with various instruments and setups",
      icon: Mic,
      color: "text-accent-600",
      bgColor: "bg-accent-50",
    },
    {
      title: "Instrumental Skills",
      description: "Proficient in guitar, piano, and other musical instruments",
      icon: Disc,
      color: "text-secondary-600",
      bgColor: "bg-secondary-50",
    },
    {
      title: "Music Production",
      description: "Recording, mixing, and mastering original compositions",
      icon: Headphones,
      color: "text-primary-600",
      bgColor: "bg-primary-50",
    },
  ];

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-6xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h1 className="font-display font-bold text-4xl md:text-5xl text-secondary-900 mb-4">
            Music & Creativity
          </h1>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Beyond software development, I'm passionate about music and creative
            expression. Here's a glimpse into my musical journey and creative
            pursuits.
          </p>
        </motion.div>

        {/* Hero Section */}
        <motion.div variants={itemVariants} className="mb-16">
          <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8 md:p-12">
            <div className="flex flex-col lg:flex-row items-center space-y-8 lg:space-y-0 lg:space-x-12">
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 p-1">
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                    <img
                      src="makinbeats.jpeg"
                      alt="Rohit making music"
                      className="w-full h-full rounded-full object-cover"
                    />
                  </div>
                </div>
              </div>
              <div className="flex-1 text-center lg:text-left">
                <h2 className="font-display font-bold text-3xl text-secondary-900 mb-4">
                  Making Beats & Creating Music
                </h2>
                <p className="text-lg text-secondary-700 leading-relaxed mb-6">
                  Music has always been a creative outlet for me. Whether it's
                  producing electronic beats, playing instruments, or
                  experimenting with new sounds, I find that music and coding
                  share similar principles of creativity, problem-solving, and
                  continuous learning.
                </p>
                <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                  <span className="bg-white px-4 py-2 rounded-full text-sm font-medium text-secondary-700 shadow-sm">
                    Electronic Music
                  </span>
                  <span className="bg-white px-4 py-2 rounded-full text-sm font-medium text-secondary-700 shadow-sm">
                    Live Performance
                  </span>
                  <span className="bg-white px-4 py-2 rounded-full text-sm font-medium text-secondary-700 shadow-sm">
                    Music Production
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Music Projects Grid */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16"
        >
          {musicProjects.map((project, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-xl shadow-lg p-6 border border-secondary-200 hover:border-primary-300 transition-all duration-300 group"
            >
              <div className="flex items-start space-x-4">
                <div
                  className={`p-4 rounded-lg ${project.bgColor} group-hover:scale-110 transition-transform duration-200`}
                >
                  <project.icon className={`w-8 h-8 ${project.color}`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-semibold text-xl text-secondary-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                    {project.title}
                  </h3>
                  <p className="text-secondary-600 leading-relaxed">
                    {project.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Creative Process */}
        <motion.div variants={itemVariants} className="mb-16">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="font-display font-semibold text-2xl text-secondary-900 mb-6 text-center">
              The Creative Process
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary-600">1</span>
                </div>
                <h4 className="font-semibold text-lg text-secondary-900 mb-2">
                  Inspiration
                </h4>
                <p className="text-secondary-600">
                  Drawing inspiration from various genres, artists, and life
                  experiences
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-accent-600">2</span>
                </div>
                <h4 className="font-semibold text-lg text-secondary-900 mb-2">
                  Creation
                </h4>
                <p className="text-secondary-600">
                  Experimenting with sounds, melodies, and arrangements to bring
                  ideas to life
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary-600">
                    3
                  </span>
                </div>
                <h4 className="font-semibold text-lg text-secondary-900 mb-2">
                  Refinement
                </h4>
                <p className="text-secondary-600">
                  Polishing and perfecting the composition through iteration and
                  feedback
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Connection to Development */}
        <motion.div variants={itemVariants} className="text-center">
          <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl p-8">
            <h3 className="font-display font-semibold text-2xl text-secondary-900 mb-4">
              Music Meets Code
            </h3>
            <p className="text-secondary-600 max-w-3xl mx-auto leading-relaxed">
              The parallels between music and software development are
              fascinating. Both require creativity, technical skill, and
              attention to detail. Whether I'm composing a melody or
              architecting a system, the process of building something from
              nothing is equally rewarding. This creative mindset enhances my
              problem-solving abilities and brings a unique perspective to my
              development work.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Music;
